import { Paperclip, X } from 'lucide-react';
import type { CommentAttachmentItem } from '../../lib/commentRemoval';
import { AttachmentImage } from './AttachmentImage';

function isImage(att: CommentAttachmentItem): boolean {
  return att.contentType.startsWith('image/');
}

/**
 * Download link for a non-image attachment — and the fallback when an image
 * fails to load, so a dead link still names the file instead of showing a
 * broken-image icon.
 *
 * `download` keeps the original name, diacritics and all: the storage key was
 * sanitised at upload, so the server can only offer `Raport-edin-.pdf`.
 */
function FileChip({ attachment }: { attachment: CommentAttachmentItem }) {
  return (
    <a
      href={attachment.url}
      download={attachment.name}
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded border border-gray-200 px-2 py-1 text-[12px] text-gray-500 hover:text-gray-800"
    >
      <Paperclip size={12} /> {attachment.name}
    </a>
  );
}

function RemoveButton({
  name,
  onClick,
  className,
}: {
  name: string;
  onClick: () => void;
  className: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Șterge „${name}”`}
      aria-label={`Șterge atașamentul ${name}`}
      className={className}
    >
      <X size={12} />
    </button>
  );
}

/**
 * The files on a task or lead comment: images as thumbnails (lightbox on
 * click), everything else as download chips. With `onRemove`, each one gets
 * an ✕ — `index` is its position in `attachments`, which is what the API takes.
 */
export function CommentAttachments({
  attachments,
  onRemove,
}: {
  attachments: CommentAttachmentItem[];
  onRemove?: (index: number, attachment: CommentAttachmentItem) => void;
}) {
  if (!attachments || attachments.length === 0) return null;
  const indexed = attachments.map((a, index) => ({ a, index }));
  const images = indexed.filter(({ a }) => isImage(a));
  const files = indexed.filter(({ a }) => !isImage(a));
  return (
    <div className="mt-2 space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {/* Shimmer while the file comes down the proxy; clicking opens the
              lightbox, with the original one click away from inside it. */}
          {images.map(({ a, index }) => (
            <div key={`${a.name}-${index}`} className="relative">
              <AttachmentImage
                src={a.url}
                alt={a.name}
                className="max-h-40 rounded-md border border-gray-200 object-cover"
                placeholderClassName="h-40 w-56"
                fallback={<FileChip attachment={a} />}
              />
              {onRemove && (
                // Over a photo of any colour: dark plate, always-white glyph
                // (`text-white` turns dark in the dark theme).
                <RemoveButton
                  name={a.name}
                  onClick={() => onRemove(index, a)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-fixed-white hover:bg-red-600"
                />
              )}
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map(({ a, index }) => (
            <span key={`${a.name}-${index}`} className="inline-flex items-center gap-0.5">
              <FileChip attachment={a} />
              {onRemove && (
                <RemoveButton
                  name={a.name}
                  onClick={() => onRemove(index, a)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-500"
                />
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
